import type { TodoItemStatus } from '@/lib/api';

/**
 * Visual language for a task's work status — the badge label + classes used
 * across the list view (TodoItemRow) and the personal "My Tasks" board. Single
 * source of truth so the two surfaces never drift.
 */
export const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: { label: 'Todo', className: 'bg-muted text-muted-foreground' },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  blocked: {
    label: 'Blocked',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  in_review: {
    label: 'In Review',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-muted text-muted-foreground line-through',
  },
};

/**
 * Work-state transitions offered in the "Set status" menu. `completed` is
 * omitted (driven by the checkbox / reviewer Verify); `in_review` is omitted too
 * — a task enters review only via "Send for review", never as a manual status;
 * `cancelled` is a separate terminal decision (see {@link CANCEL_STATUS}).
 */
export const WORK_STATUSES: { value: TodoItemStatus; label: string }[] = [
  { value: 'pending', label: 'Todo' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
];

export const CANCEL_STATUS: { value: TodoItemStatus; label: string } = {
  value: 'cancelled',
  label: 'Cancelled',
};

/**
 * The manager-only "mark Completed" shortcut offered in the "Set status" menu on
 * a review-required list. It lets an owner/admin bypass the review step (acting
 * as their own reviewer); everyone else must go through Send-for-review → Verify.
 */
export const COMPLETE_STATUS: { value: TodoItemStatus; label: string } = {
  value: 'completed',
  label: 'Completed',
};
